/** All cadence mutations — CRUD, events, members, sharing, metrics, rules, jobs. */

import {
  CADENCE_FIELDS,
  CADENCE_EVENT_FIELDS,
  CADENCE_EDGE_FIELDS,
  AUTO_REMOVAL_RULE_FIELDS,
  SUCCESS_METRIC_FIELDS,
  CADENCE_MEMBER_FIELDS,
} from './fragments';

// -- Cadence CRUD --

export const UPDATE_CADENCE_WITH_CONFIG_MUTATION = `
  mutation UpdateCadenceWithConfig($cadenceId: ID!, $version: Int!, $input: UpdateCadenceWithConfigInput!) {
    updateCadenceWithConfig(cadenceId: $cadenceId, version: $version, input: $input) {
      success
      message
      version
      cadence {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
        successMetrics {
          ${SUCCESS_METRIC_FIELDS}
        }
        autoRemovalRules {
          ${AUTO_REMOVAL_RULE_FIELDS}
        }
        # See CADENCE_QUERY: without this, the mutation response reports the
        # proposals it was just asked to save as empty (NEXDEV-2120).
        linkedProposals {
          id
          proposalId
        }
      }
    }
  }
`;

export const CREATE_CADENCE_WITH_CONFIG_MUTATION = `
  mutation CreateCadenceWithConfig($input: CreateCadenceWithConfigInput!) {
    createCadenceWithConfig(input: $input) {
      success
      message
      cadence {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
        successMetrics {
          ${SUCCESS_METRIC_FIELDS}
        }
        autoRemovalRules {
          ${AUTO_REMOVAL_RULE_FIELDS}
        }
        # See CADENCE_QUERY: without this, the mutation response reports the
        # proposals it was just asked to save as empty (NEXDEV-2120).
        linkedProposals {
          id
          proposalId
        }
      }
    }
  }
`;

export const CREATE_CADENCE_MUTATION = `
  mutation CreateCadence($input: CreateCadenceInput!) {
    createCadence(input: $input) {
      success
      message
      cadence {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
      }
    }
  }
`;

export const UPDATE_CADENCE_MUTATION = `
  mutation UpdateCadence($id: ID!, $version: Int!, $input: UpdateCadenceInput!) {
    updateCadence(id: $id, version: $version, input: $input) {
      success
      message
      cadence {
        ${CADENCE_FIELDS}
      }
    }
  }
`;

export const DELETE_CADENCE_MUTATION = `
  mutation DeleteCadence($id: ID!, $version: Int!) {
    deleteCadence(id: $id, version: $version) {
      success
      message
    }
  }
`;

export const ACTIVATE_CADENCE_MUTATION = `
  mutation ActivateCadence($id: ID!, $version: Int!) {
    activateCadence(id: $id, version: $version) {
      success
      message
      cadence {
        ${CADENCE_FIELDS}
      }
    }
  }
`;

// Pulls a cadence back to DRAFT. For bulk messages this is the "Cancel Send"
// action: the backend also cancels the parked BackgroundJobWorkflow and any live
// per-member workflows, and preserves scheduledSendDate so re-publishing keeps
// the same schedule. Eligibility rules live in utils/cadence-cancel-send.js.
export const REVERT_CADENCE_TO_DRAFT_MUTATION = `
  mutation RevertCadenceToDraft($id: ID!, $version: Int!) {
    revertCadenceToDraft(id: $id, version: $version) {
      success
      message
      version
      cadence {
        ${CADENCE_FIELDS}
      }
    }
  }
`;

export const RESTORE_CADENCE_MUTATION = `
  mutation RestoreCadence($id: ID!) {
    restoreCadence(id: $id) {
      success
      message
      cadence {
        id
        name
        version
      }
      version
    }
  }
`;

// -- Events --

export const CREATE_CADENCE_EVENTS_MUTATION = `
  mutation CreateCadenceEvents($cadenceId: ID!, $cadenceVersion: Int!, $startingEventId: ID!, $events: [CreateCadenceEventInput!]!, $edgeType: CadenceEdgeType!) {
    createCadenceEvents(cadenceId: $cadenceId, cadenceVersion: $cadenceVersion, startingEventId: $startingEventId, events: $events, edgeType: $edgeType) {
      success
      message
      cadence {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
      }
    }
  }
`;

export const DELETE_CADENCE_EVENTS_MUTATION = `
  mutation DeleteCadenceEvents($cadenceId: ID!, $cadenceVersion: Int!, $firstEventId: ID!, $lastEventId: ID!, $eventIds: [ID!]!) {
    deleteCadenceEvents(cadenceId: $cadenceId, cadenceVersion: $cadenceVersion, firstEventId: $firstEventId, lastEventId: $lastEventId, eventIds: $eventIds) {
      success
      message
      cadence {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
      }
    }
  }
`;

export const UPDATE_CADENCE_EVENT_MUTATION = `
  mutation UpdateCadenceEvent($cadenceId: ID!, $eventId: ID!, $cadenceVersion: Int!, $input: UpdateCadenceEventInput!) {
    updateCadenceEvent(cadenceId: $cadenceId, eventId: $eventId, cadenceVersion: $cadenceVersion, input: $input) {
      success
      message
      event {
        ${CADENCE_EVENT_FIELDS}
      }
      cadenceVersion
    }
  }
`;

export const COMPLETE_MANUAL_STEP_MUTATION = `
  mutation CompleteManualStep($cadenceMemberId: ID!, $cadenceEventId: ID!) {
    completeManualStep(cadenceMemberId: $cadenceMemberId, cadenceEventId: $cadenceEventId) {
      success
      message
    }
  }
`;

export const SKIP_CADENCE_EVENT_MUTATION = `
  mutation SkipCadenceEvent($cadenceId: ID!, $memberId: ID!) {
    skipCadenceEvent(cadenceId: $cadenceId, memberId: $memberId) {
      success
    }
  }
`;

// -- Members --

export const ENROLL_MEMBER_MUTATION = `
  mutation EnrollMember($input: EnrollCadenceMemberInput!) {
    enrollMember(input: $input) {
      success
      message
      cadenceMember {
        id
        contactId
        status
      }
    }
  }
`;

export const REMOVE_CADENCE_MEMBER_MUTATION = `
  mutation RemoveCadenceMember($input: RemoveCadenceMemberInput!) {
    removeCadenceMember(input: $input) {
      cadenceMember {
        ${CADENCE_MEMBER_FIELDS}
      }
    }
  }
`;

export const BULK_ENROLL_MEMBERS_MUTATION = `
  mutation BulkEnrollMembers($input: BulkEnrollCadenceMembersInput!) {
    bulkEnrollMembers(input: $input) {
      success
      message
      job { id jobType status progress totalItems processedItems }
    }
  }
`;

export const SAVE_INDIVIDUAL_CADENCE_AUDIENCES_MUTATION = `
  mutation SaveIndividualCadenceAudiences($cadenceId: ID!, $contactIds: [Int!]!) {
    saveIndividualCadenceAudiences(cadenceId: $cadenceId, contactIds: $contactIds) {
      success
      message
      savedCount
    }
  }
`;

// -- Sharing --

export const SHARE_CADENCE_MUTATION = `
  mutation ShareCadence($input: ShareCadenceInput!) {
    shareCadence(input: $input) {
      sharing {
        id
        cadenceId
        userId
        shareLevel
        canView
        canEdit
        canDelete
      }
      cadenceVersion
    }
  }
`;

export const REVOKE_CADENCE_SHARING_MUTATION = `
  mutation RevokeCadenceSharing($id: ID!, $cadenceId: ID!, $cadenceVersion: Int!) {
    revokeCadenceSharing(id: $id, cadenceId: $cadenceId, cadenceVersion: $cadenceVersion) {
      success
      cadenceVersion
    }
  }
`;

export const UPDATE_CADENCE_SHARING_MUTATION = `
  mutation UpdateCadenceSharing($id: ID!, $cadenceId: ID!, $cadenceVersion: Int!, $input: UpdateCadenceSharingInput!) {
    updateCadenceSharing(id: $id, cadenceId: $cadenceId, cadenceVersion: $cadenceVersion, input: $input) {
      sharing {
        id
        cadenceId
        userId
        shareLevel
        canView
        canEdit
        canDelete
      }
      cadenceVersion
    }
  }
`;

export const CADENCE_SHARING_SETTINGS_QUERY = `
  query CadenceSharingSettings($cadenceId: ID!) {
    cadenceSharingSettings(cadenceId: $cadenceId) {
      id
      cadenceId
      userId
      shareLevel
      canView
      canEdit
      canDelete
    }
  }
`;

// -- Success Metrics --

export const CREATE_SUCCESS_METRIC_MUTATION = `
  mutation CreateSuccessMetric($input: CreateSuccessMetricInput!) {
    createSuccessMetric(input: $input) {
      successMetric {
        ${SUCCESS_METRIC_FIELDS}
      }
    }
  }
`;

export const UPDATE_SUCCESS_METRIC_MUTATION = `
  mutation UpdateSuccessMetric($id: ID!, $cadenceId: ID!, $input: UpdateSuccessMetricInput!) {
    updateSuccessMetric(id: $id, cadenceId: $cadenceId, input: $input) {
      successMetric {
        ${SUCCESS_METRIC_FIELDS}
      }
    }
  }
`;

export const DELETE_SUCCESS_METRIC_MUTATION = `
  mutation DeleteSuccessMetric($id: ID!, $cadenceId: ID!) {
    deleteSuccessMetric(id: $id, cadenceId: $cadenceId) {
      success
    }
  }
`;

export const RETRY_BLOCKED_MEMBER_MUTATION = `
  mutation RetryBlockedMember($memberId: ID!) {
    retryBlockedCadenceMember(memberId: $memberId) {
      success
      errors
      cadenceMember {
        id
        status
      }
      workflowId
    }
  }
`;

export const RECALCULATE_CADENCE_STATS_MUTATION = `
  mutation RecalculateCadenceStats($cadenceId: ID!) {
    recalculateCadenceStats(cadenceId: $cadenceId) {
      success
    }
  }
`;

export const REEVALUATE_SUCCESS_METRICS_MUTATION = `
  mutation ReevaluateCadenceSuccessMetrics($cadenceId: ID!) {
    reevaluateCadenceSuccessMetrics(cadenceId: $cadenceId) {
      success
      evaluated
      newlySuccessful
      log
    }
  }
`;

export const REFRESH_CADENCE_SAVED_SEARCHES_MUTATION = `
  mutation RefreshCadenceSavedSearches($cadenceId: ID!) {
    refreshCadenceSavedSearches(cadenceId: $cadenceId) {
      success
      mode
      requested
      succeeded
      failed
      listIds
      failedListIds
    }
  }
`;

// -- Auto Removal Rules --

export const CREATE_AUTO_REMOVAL_RULE_MUTATION = `
  mutation CreateAutoRemovalRule($input: CreateAutoRemovalRuleInput!) {
    createAutoRemovalRule(input: $input) {
      autoRemovalRule {
        ${AUTO_REMOVAL_RULE_FIELDS}
      }
    }
  }
`;

export const UPDATE_AUTO_REMOVAL_RULE_MUTATION = `
  mutation UpdateAutoRemovalRule($id: ID!, $cadenceId: ID!, $input: UpdateAutoRemovalRuleInput!) {
    updateAutoRemovalRule(id: $id, cadenceId: $cadenceId, input: $input) {
      autoRemovalRule {
        ${AUTO_REMOVAL_RULE_FIELDS}
      }
    }
  }
`;

export const DELETE_AUTO_REMOVAL_RULE_MUTATION = `
  mutation DeleteAutoRemovalRule($id: ID!, $cadenceId: ID!) {
    deleteAutoRemovalRule(id: $id, cadenceId: $cadenceId) {
      success
    }
  }
`;

// -- Templates & Duplication --

export const SAVE_CADENCE_AS_TEMPLATE_MUTATION = `
  mutation SaveCadenceAsTemplate($cadenceId: ID!, $input: SaveCadenceAsTemplateInput!) {
    saveCadenceAsTemplate(cadenceId: $cadenceId, input: $input) {
      template {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
      }
    }
  }
`;

export const CREATE_CADENCE_FROM_TEMPLATE_MUTATION = `
  mutation CreateCadenceFromTemplate($templateId: ID!, $input: CreateCadenceFromTemplateInput!) {
    createCadenceFromTemplate(templateId: $templateId, input: $input) {
      cadence {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
      }
    }
  }
`;

export const DUPLICATE_CADENCE_MUTATION = `
  mutation DuplicateCadence($cadenceId: ID!, $input: DuplicateCadenceInput) {
    duplicateCadence(cadenceId: $cadenceId, input: $input) {
      cadence {
        ${CADENCE_FIELDS}
        events {
          ${CADENCE_EVENT_FIELDS}
        }
        edges {
          ${CADENCE_EDGE_FIELDS}
        }
        successMetrics {
          ${SUCCESS_METRIC_FIELDS}
        }
        autoRemovalRules {
          ${AUTO_REMOVAL_RULE_FIELDS}
        }
        linkedProposals {
          id
          proposalId
        }
      }
    }
  }
`;

export const SYNC_CADENCE_PROPOSALS_MUTATION = `
  mutation SyncCadenceProposals($cadenceId: ID!, $proposalIds: [Int!]!) {
    syncCadenceProposals(cadenceId: $cadenceId, proposalIds: $proposalIds) {
      success
      message
      proposalIds
    }
  }
`;

// -- Audiences --

export const CREATE_CADENCE_LIST_AUDIENCE_MUTATION = `
  mutation CreateCadenceListAudience($input: CreateCadenceListAudienceInput!) {
    createCadenceListAudience(input: $input) {
      success
      message
      audienceId
      cadence { id status version }
    }
  }
`;

export const CREATE_CADENCE_AUDIENCE_MUTATION = `
  mutation CreateCadenceAudience($input: CreateCadenceAudienceInput!) {
    createCadenceAudience(input: $input) {
      success
      message
      audienceId
      cadence { id status version }
    }
  }
`;

export const UPDATE_CADENCE_AUDIENCE_MUTATION = `
  mutation UpdateCadenceAudience($audienceId: ID!, $input: UpdateCadenceAudienceInput!) {
    updateCadenceAudience(audienceId: $audienceId, input: $input) {
      success
      message
      cadence { id status version }
    }
  }
`;

export const RESYNC_CADENCE_AUDIENCE_MUTATION = `
  mutation ResyncCadenceAudience($audienceId: ID!) {
    resyncCadenceAudience(audienceId: $audienceId) {
      success
      message
      audienceId
      cadence { id status version }
    }
  }
`;

export const DELETE_CADENCE_AUDIENCE_MUTATION = `
  mutation DeleteCadenceAudience($audienceId: ID!) {
    deleteCadenceAudience(audienceId: $audienceId) {
      success
      message
    }
  }
`;

export const UPDATE_CADENCE_SENDER_CONFIG_MUTATION = `
  mutation UpdateCadenceSenderConfig($cadenceId: ID!, $cadenceVersion: Int!, $fromEmail: String, $fromName: String, $replyToEmails: [String!]) {
    updateCadenceSenderConfig(cadenceId: $cadenceId, cadenceVersion: $cadenceVersion, fromEmail: $fromEmail, fromName: $fromName, replyToEmails: $replyToEmails) {
      success
      message
      cadenceVersion
    }
  }
`;

// -- Jobs --

export const JOB_QUERY = `
  query Job($id: ID!) {
    job(id: $id) {
      id jobType status progress totalItems processedItems errorMessage completedAt
    }
  }
`;

export const JOBS_QUERY = `
  query Jobs($cadenceId: ID, $jobType: CadenceJobType, $status: CadenceJobStatus, $limit: Int) {
    jobs(cadenceId: $cadenceId, jobType: $jobType, status: $status, limit: $limit) {
      id jobType status progress totalItems processedItems errorMessage completedAt
    }
  }
`;

export const CREATE_TASK_CLEANUP_JOB_MUTATION = `
  mutation CreateTaskCleanupJob($cadenceIds: [ID!]!) {
    createTaskCleanupJob(cadenceIds: $cadenceIds) {
      success
      job {
        id jobType status progress totalItems processedItems errorMessage completedAt
      }
    }
  }
`;

// -- Send Preferences --

export const UPDATE_CADENCE_SEND_PREFERENCES_MUTATION = `
  mutation UpdateCadenceSendPreferences($input: UpdateCadenceSendPreferencesInput!, $version: Int) {
    updateCadenceSendPreferences(input: $input, version: $version) {
      success
      sendPreferences {
        id
        cadenceId
        version
      }
      version
    }
  }
`;
